<template>
  <div class="page-content">
    <div class="headings">
      <h2 class="title title-primary">
        {{ $t("lang.inbound.containers.page_title_line_1") }}
      </h2>
      <span>|</span>
      <h3 class="title title-primary">
        {{ $t("lang.inbound.containers.page_title_line_2") }}
      </h3>
    </div>
    <div class="page-header">
      <div class="boxes rows" v-if="!shouldLoading">
        <bx-box
          dark
          color="outline"
          no-padding
          class="col-12 col-lg-3 col-md-4 p-1"
        >
          <bx-complementary
            v-if="stock.total !== undefined"
            :total="stock.total"
            :title="$t('lang.inbound.containers.box_title_1')"
            :left="stock.left"
            :right="stock.right"
          />
        </bx-box>

        <bx-box dark color="outline" class="col-6 col-lg-auto col-md-4 p-1">
          <bx-value
            :label="$t('lang.inbound.containers.box_label_2')"
            :value="receptionTime"
            font-size="xs"
          />
        </bx-box>

        <bx-box dark color="outline" class="col-6 col-lg-auto col-md-4 p-1">
          <bx-value
            :label="$t('lang.inbound.containers.box_label_3')"
            :value="maxReceptionTime"
            font-size="xs"
          />
        </bx-box>

        <bx-box
          dark
          color="outline"
          class="col-4 col-lg-auto col-md-4 p-1 bx-box-adjustable"
        >
          <bx-value
            :label="$t('lang.inbound.containers.box_label_4')"
            :value="delayAvgReceptionTime"
            font-size="xs"
            valueColor="red"
          />
        </bx-box>

        <bx-box dark color="outline" class="col-4 col-lg-auto col-md-4 p-1">
          <bx-value
            :label="$t('lang.inbound.containers.box_label_5')"
            :value="orderPerDay"
            font-size="xs"
          />
        </bx-box>

        <bx-box dark color="outline" class="col-4 col-lg-auto col-md-4 p-1">
          <bx-value
            :label="$t('lang.inbound.containers.box_label_6')"
            :value="orderIncorrectPerDay"
            font-size="xs"
            valueColor="red"
          />
        </bx-box>
      </div>
      <skeleton-boxes progress-box :boxes="5" v-else />
    </div>

    <div class="page-main-content">
      <div class="container-fluid">
        <div class="row">
          <bx-box
            :title="
              ordersCount + ' ' + $t('lang.inbound.containers.table_title')
            "
            color="white"
            no-padding
            class="col-12"
            :loading="shouldLoading"
          >
            <template v-slot:default>
              <bx-table
                :data="data"
                :columns="columns"
                paginated
                backendPagination
                :currentPage="page"
                :perPage="itemsPerPage"
                :total="ordersCount"
                @search="onSearch"
                @update:currentPage="updatePage"
                v-if="!shouldLoading"
              >
                <template slot-scope="props">
                  <bx-table-column field="destination_status" centered>
                    <bx-order-status
                      :status="props.row.destination_status"
                    ></bx-order-status>
                  </bx-table-column>

                  <bx-table-column field="waiting_time" centered>
                    <strong :class="props.row.time_color">{{
                      props.row.waiting_time
                    }}</strong>
                  </bx-table-column>
                  <bx-table-column centered>
                    <router-link
                      :to="
                        `/inbound/container/${props.row.container_code}/detail`
                      "
                      class="bx-link bx-link-bold"
                      >{{ props.row.container_code }}</router-link
                    >
                  </bx-table-column>
                  <bx-table-column centered>{{
                    formatDate(props.row.arrival_date)
                  }}</bx-table-column>
                  <bx-table-column centered>{{
                    props.row.theorical_item_count
                  }}</bx-table-column>

                  <bx-table-column centered>{{
                    props.row.real_item_count
                  }}</bx-table-column>

                  <bx-table-column centered>
                    <router-link
                      :to="`/inbound/provider/${props.row.origin}/detail`"
                      class="bx-link bx-link-bold"
                      >{{ props.row.provider_name }}</router-link
                    >
                  </bx-table-column>
                </template>
              </bx-table>
            </template>
          </bx-box>
        </div>
        <v-row class="d-flex d-sm-none">
          <div
            v-infinite-scroll="loadMore"
            infinite-scroll-disabled="busy"
            infinite-scroll-distance="20"
            style="width: 100%"
          >
            <v-list width="100%">
              <v-list-item
                two-line
                v-for="(item, index) in dataList"
                :key="index"
                class="green lighten-5 ma-2 rounded-lg"
                ripple
                :to="`/inbound/container/${item.id}/detail`"
              >
                <v-list-item-content>
                  <v-list-item-title>
                    <div>
                      {{ item.id }}
                    </div>
                  </v-list-item-title>
                  <v-list-item-subtitle>{{
                    item.provider_name
                  }}</v-list-item-subtitle>
                  <v-list-item-subtitle class="text-caption">{{
                    formatDate(item.arrival_date)
                  }}</v-list-item-subtitle>
                </v-list-item-content>
                <v-list-item-action>
                  <div>
                    <img
                      v-if="item.destination_status === 2"
                      style="height: 20px; width: 20px"
                      src="@/assets/i/triangle-outline.svg"
                    />
                    <bx-tag v-if="item.destination_status == 3"
                      ><i class="fa fa-check green-icon"></i
                    ></bx-tag>
                  </div>
                  <v-list-item-subtitle class="text-caption">
                    {{ item.real_item_count + "/" + item.theorical_item_count }}
                  </v-list-item-subtitle>
                  <v-list-item-subtitle class="text-caption">
                    {{ item.waiting_time }}
                  </v-list-item-subtitle>
                </v-list-item-action>
              </v-list-item>
            </v-list>
          </div>
        </v-row>
        <skeleton-table :columns="7" v-if="shouldLoading" />
        <!-- <v-row>
          <bx-box
            :title="$t('lang.inbound.containers.line_graph_title')"
            color="white"
            no-padding
            class="col-12"
            v-if="!isMobile"
          >
            <bx-line-chart :chartData="evolutionData" />
          </bx-box>
        </v-row> -->
      </div>
    </div>
  </div>
</template>

<script>
// import c from '../colors'
// import months from '../months'
import BxTableColumn from "../../bx-components/BxTableColumn";
import moment from "moment";
import common from "@/utils/common";

var loading = false;
export default {
  name: "inbound-containers",
  components: { BxTableColumn },
  mixins: [common],
  data() {
    return {
      key: "inbound_container",
      shouldLoading: true,
      columns: [],
      data: [],
      dataList: [],
      ordersCount: "",
      receptionTime: "",
      maxReceptionTime: "",
      delayAvgReceptionTime: "",
      orderPerDay: "",
      orderIncorrectPerDay: "",
      inboundData: {},
      outboundData: {},
      evolutionData: {},
      stock: {},
      API_BASE: process.env.VUE_APP_API_BASE,
      reloadInterval: null,
      locationId: null,
      itemsPerPage: process.env.VUE_APP_ITEMS_PER_PAGE,
      page: 1,
      filters: {
        order__code__contains: null,
        origin__name__contains: null,
        arrival_date__contains: null
      }
    };
  },
  methods: {
    loadMore() {
      if (
        !this.shouldLoading &&
        this.dataList.length &&
        this.dataList.length < this.ordersCount &&
        this.isMobile
      ) {
        this.page++;
        this.getContainers(this.locationId);
      }
    },
    formatDate(date) {
      return date
        ? moment(date).format("DD/ MM/ YYYY, h:mm a")
        : this.$t("lang.messages.no_date_available");
    },
    onSearch(field, value) {
      this.$session.set(this.key + field, value);

      switch (field) {
        case "container_code":
          this.filters.containerCode = value;
          this.getData();
          break;
        case "origin":
          this.filters.origin = value;
          this.getData();
          break;
        case "arrivalDate":
          this.filters.arrivalDate = value;
          this.getData();
          break;
      }
    },
    updatePage(p) {
      this.page = p;
      this.getData(this.locationId);
    },
    async getContainers(locationId) {
      try {
        // Load orders
        const orders = await this.$api.containers.list(locationId, {
          offset: (this.page - 1) * this.itemsPerPage,
          limit: this.itemsPerPage,
          container_code__contains: this.filters.containerCode
        });

        this.ordersCount = orders.count;
        this.data = orders.results.map(item => {
          let isAfter = moment().isAfter(item.estimated_arrival_date);

          return {
            id: item.id,
            container_code: item.container_code,
            origin_status: item.origin_status,
            destination_status: item.destination_status,
            waiting_time: item.waiting_time,
            theorical_item_count: item.theorical_item_count,
            real_item_count: item.real_item_count,
            num_boxes: item.num_boxes,
            num_verified_boxes: item.num_verified_boxes,
            creation_date: item.creation_date,
            estimated_arrival_date: item.estimated_arrival_date,
            arrival_date: item.arrival_date,
            origin: item.origin,
            provider_name: item.provider_name,
            time_color: isAfter ? "bx-text-red" : "bx-text-black"
          };
        });

        if (this.isMobile) {
          this.dataList = this.dataList.concat(this.data);
        }

        this.columns = [
          {
            field: "destination_status",
            label: this.$t("lang.inbound.containers.table_field_1"),
            centered: true,
            width: 80
          },
          {
            field: "waiting_time",
            label: this.$t("lang.inbound.containers.table_field_2"),
            centered: true
          },
          {
            field: "container_code",
            label: this.$t("lang.inbound.containers.table_field_3"),
            filter: {
              placeholder: this.$t(
                "lang.inbound.containers.table_field_placeholder_3"
              ),
              value: this.filters.containerCode
            },
            centered: true
          },
          {
            field: "arrivalDate",
            label: this.$t("lang.inbound.containers.table_field_4"),
            filter: {
              placeholder: this.$t(
                "lang.inbound.containers.table_field_placeholder_4"
              ),
              value: this.filters.arrivalDate,
              isdate: true
            },
            centered: true
          },
          {
            field: "item_count",
            label: this.$t("lang.inbound.containers.table_field_5"),
            centered: true
          },
          {
            field: "real_item_count",
            label: this.$t("lang.inbound.containers.table_field_6"),
            centered: true
          },
          {
            field: "origin",
            label: this.$t("lang.inbound.containers.table_field_7"),
            filter: {
              placeholder: this.$t(
                "lang.inbound.containers.table_field_placeholder_7"
              ),
              value: this.filters.origin,
              isdate: false
            },
            centered: true
          }
        ];
      } catch (error) {
        // console.log(error)
      } finally {
        this.shouldLoading = false;
      }
    },
    async getData() {
      try {
        // Load stats
        if (!loading) {
          loading = true;
          let location = localStorage.getItem("location") || "";
          if (!location) {
            try {
              let firstLocation = await this.$api.location.search("", {
                limit: 1,
                offset: 0,
                orderBy: "id"
              });
              location = firstLocation.results[0].description;
            } catch (error) {
              console.log(error);
            }
          }
          this.locationId = localStorage.getItem("location_id");
          const inboundStats = await this.$api.location.inboundStats(
            this.locationId
          );
          this.delayAvgReceptionTime = inboundStats.average_delay;
          this.orderPerDay = inboundStats.daily_orders;
          this.receptionTime = inboundStats.reception_time;
          this.maxReceptionTime = inboundStats.max_time;
          this.orderIncorrectPerDay = inboundStats.incorrect_daily_orders;

          // Load orders
          await this.getContainers(this.locationId);

          this.stock = {
            total: inboundStats.total_orders_for_today,
            left: {
              value:
                inboundStats.total_orders_for_today -
                inboundStats.total_pending_orders_for_today,
              label: this.$t("lang.inbound.containers.box_left_1"),
              icon: require("@/assets/i/check.png")
            },
            right: {
              value: inboundStats.total_pending_orders_for_today,
              label: this.$t("lang.inbound.containers.box_right_1"),
              icon: require("@/assets/i/ordersTime.png")
            }
          };

          // Evolution data
          // this.evolutionData = {
          //   labels: months,
          //   datasets: [
          //     {
          //       label: this.$t('lang.inbound.containers.line_graph_label_1'),
          //       backgroundColor: c.getRGBA(c.lime, 0.6),
          //       borderColor: c.lime,

          //       data: inboundStats.correctly_received_porders
          //     },
          //     {
          //       label: this.$t('lang.inbound.containers.line_graph_label_2'),
          //       backgroundColor: c.getRGBA(c.red, 0.6),
          //       borderColor: c.red,
          //       data: inboundStats.incorrectly_received_porders
          //     }
          //   ]
          // }
          loading = false;
          this.shouldLoading = false;
        }
      } catch (error) {
        // console.log(error)
      }
    }
  },
  mounted() {
    this.filters.containerCode = this.$session.get(this.key + "container_code");
    this.filters.origin = this.$session.get(this.key + "origin");
    this.filters.arrivalDate = this.$session.get(this.key + "arrivalDate");
    if (!this.isMobile) {
      this.reloadInterval = setInterval(
        this.getData,
        process.env.VUE_APP_RELOAD_TIME
      );
    }
    this.getData();
  },
  beforeDestroy() {
    clearInterval(this.reloadInterval);
  }
};
</script>

<style scoped>
@media screen and (max-width: 578px) {
  .bx-box-adjustable >>> .bx-box-content {
    min-height: 89px;
  }

  .bx-box-adjustable >>> .bx-value-value {
    margin-top: 10px;
  }
}
</style>

<style scoped>
@media screen and (max-width: 411px) {
  .bx-box-adjustable >>> .bx-box-content {
    min-height: 79px;
  }

  .bx-box-adjustable >>> .bx-value-value {
    margin-top: 0px !important;
  }
}
</style>
